import messageTemplates from "@/assets/messageTemplates.json";

const STORAGE_KEYS = {
  SMS_INDEX: "smsTemplateIndex",
  DOCTOR_INDEX: "doctorTemplateIndex",
  PATIENT_REG_INDEX: "patientRegTemplateIndex"
};

class MessageTemplateManager {
  constructor() {
    this.templates = messageTemplates;
  }

  /**
   * Get the next template for SMS messages
   * @param {Object} variables - Variables to replace in template (patientName, reportName, etc.)
   * @returns {string} - Formatted message
   */
  getNextSmsTemplate(variables = {}) {
    const currentIndex = this.getCurrentIndex(STORAGE_KEYS.SMS_INDEX);
    const template = this.templates.smsTemplates[currentIndex];
    const nextIndex = (currentIndex + 1) % this.templates.smsTemplates.length;

    this.setCurrentIndex(STORAGE_KEYS.SMS_INDEX, nextIndex);

    return this.formatMessage(template.message, variables);
  }

  /**
   * Get the next template for doctor messages
   * @param {Object} variables - Variables to replace in template (patientName, reportName, etc.)
   * @returns {string} - Formatted message
   */
  getNextDoctorTemplate(variables = {}) {
    const currentIndex = this.getCurrentIndex(STORAGE_KEYS.DOCTOR_INDEX);
    const template = this.templates.doctorMessageTemplates[currentIndex];
    const nextIndex =
      (currentIndex + 1) % this.templates.doctorMessageTemplates.length;

    this.setCurrentIndex(STORAGE_KEYS.DOCTOR_INDEX, nextIndex);

    return this.formatMessage(template.message, variables);
  }

  /**
   * Get the next template for patient registration messages
   * @param {Object} variables - Variables to replace in template (patientName, mrNo, etc.)
   * @returns {string} - Formatted message
   */
  getNextPatientRegistrationTemplate(variables = {}) {
    const currentIndex = this.getCurrentIndex(STORAGE_KEYS.PATIENT_REG_INDEX);
    const template = this.templates.patientRegistrationTemplates[currentIndex];
    const nextIndex =
      (currentIndex + 1) % this.templates.patientRegistrationTemplates.length;

    this.setCurrentIndex(STORAGE_KEYS.PATIENT_REG_INDEX, nextIndex);

    return this.formatMessage(template.message, variables);
  }

  /**
   * Get a random template for SMS messages
   * @param {Object} variables - Variables to replace in template
   * @returns {string} - Formatted message
   */
  getRandomSmsTemplate(variables = {}) {
    const randomIndex = Math.floor(
      Math.random() * this.templates.smsTemplates.length
    );
    const template = this.templates.smsTemplates[randomIndex];
    return this.formatMessage(template.message, variables);
  }

  /**
   * Get a random template for doctor messages
   * @param {Object} variables - Variables to replace in template
   * @returns {string} - Formatted message
   */
  getRandomDoctorTemplate(variables = {}) {
    const randomIndex = Math.floor(
      Math.random() * this.templates.doctorMessageTemplates.length
    );
    const template = this.templates.doctorMessageTemplates[randomIndex];
    return this.formatMessage(template.message, variables);
  }

  /**
   * Get a random template for patient registration messages
   * @param {Object} variables - Variables to replace in template
   * @returns {string} - Formatted message
   */
  getRandomPatientRegistrationTemplate(variables = {}) {
    const randomIndex = Math.floor(
      Math.random() * this.templates.patientRegistrationTemplates.length
    );
    const template = this.templates.patientRegistrationTemplates[randomIndex];
    return this.formatMessage(template.message, variables);
  }

  /**
   * Format message by replacing variables
   * @param {string} message - Template message
   * @param {Object} variables - Variables to replace
   * @returns {string} - Formatted message
   */
  formatMessage(message, variables = {}) {
    let formattedMessage = message;

    // Replace variables in the format {variableName}
    Object.keys(variables).forEach(key => {
      const placeholder = `{${key}}`;
      const value = variables[key] || "";
      formattedMessage = formattedMessage.replace(
        new RegExp(placeholder, "g"),
        value
      );
    });

    return formattedMessage;
  }

  /**
   * Get current index from localStorage
   * @param {string} key - Storage key
   * @returns {number} - Current index
   */
  getCurrentIndex(key) {
    const stored = localStorage.getItem(key);
    return stored ? parseInt(stored, 10) : 0;
  }

  /**
   * Set current index in localStorage
   * @param {string} key - Storage key
   * @param {number} index - Index to store
   */
  setCurrentIndex(key, index) {
    localStorage.setItem(key, index.toString());
  }

  /**
   * Reset all template indices to 0
   */
  resetAllIndices() {
    this.setCurrentIndex(STORAGE_KEYS.SMS_INDEX, 0);
    this.setCurrentIndex(STORAGE_KEYS.DOCTOR_INDEX, 0);
    this.setCurrentIndex(STORAGE_KEYS.PATIENT_REG_INDEX, 0);
  }

  /**
   * Get current indices for debugging
   * @returns {Object} - Current indices
   */
  getCurrentIndices() {
    return {
      sms: this.getCurrentIndex(STORAGE_KEYS.SMS_INDEX),
      doctor: this.getCurrentIndex(STORAGE_KEYS.DOCTOR_INDEX),
      patientReg: this.getCurrentIndex(STORAGE_KEYS.PATIENT_REG_INDEX)
    };
  }

  /**
   * Get all available templates for a specific type
   * @param {string} type - Template type ('sms', 'doctor', 'patientReg')
   * @returns {Array} - Array of templates
   */
  getTemplatesByType(type) {
    switch (type) {
      case "sms":
        return this.templates.smsTemplates;
      case "doctor":
        return this.templates.doctorMessageTemplates;
      case "patientReg":
        return this.templates.patientRegistrationTemplates;
      default:
        return [];
    }
  }
}

// Create and export a singleton instance
const messageTemplateManager = new MessageTemplateManager();
export default messageTemplateManager;
